import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { OogwaiLogo2 } from "./Icons/iconImages";
import gsap from "gsap";
import { Progress } from "antd";

const Loading = () => {
  const { isAuth } = useSelector((state) => state.auth);
  const apiLoading = useSelector((state) => state.baseApiReducer.config.online);
  const logoRef = useRef(null);
  const textRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (apiLoading) {
      // Animate the fill effect when loading starts
      gsap.to(overlayRef.current, {
        height: "20%", // Start filling
        duration: 1.5,
        ease: "power2.out",
      });
    } else {
      // Complete the animation when loading is done
      gsap.to(overlayRef.current, {
        height: "0%", // Fully reveal the logo
        duration: 2.5,
        ease: "power2.out",
      });

      gsap.to(textRef.current, {
        opacity: 1,
        duration: 1,
      });

      // Optional: Fade out the loading screen
      gsap.to(logoRef.current, {
        opacity: 0,
        duration: 2,
        ease: "power2.out",
        delay: 1,
      });
    }
  }, [apiLoading]);

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative">
        {/* Logo Container */}
        <div ref={logoRef} className="relative">
          <OogwaiLogo2 className="m-auto relative animate-pulse" />
          {/* Overlay for opacity fill effect */}
          <div
            ref={overlayRef}
            className="absolute inset-0 bg-white"
            style={{ opacity: 0.8 }}
          ></div>
        </div>

        {/* Loading Text */}
        {isAuth && apiLoading && (
          <span
            ref={textRef}
            className="inline-block mt-1 font-medium text-sm opacity-50"
          >
            Loading...
          </span>
        )}
      </div>
    </div>
  );
};

export const FakeProgressBar = ({ isLoading, isSuccess }) => {
  const [percent, setPercent] = useState(0);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    console.log('Loading:', isLoading, 'Success:', isSuccess);

    // Detect new load session even if isSuccess is still true
    if (isLoading && !wasLoadingRef.current) {
      wasLoadingRef.current = true;

      if (timeoutRef.current) {
        console.log('Clearing timeout');
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setVisible(true);
      setPercent(0);
      console.log('Start loading, percent reset');

      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setPercent(prev => {
          const next = prev + Math.ceil(Math.random() * 10);
          console.log('Progressing:', next);
          return next < 100 ? next : 99;
        });
      }, 300);
    }

    if (!isLoading && wasLoadingRef.current) {
      console.log('Clearing interval because isLoading is false');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      wasLoadingRef.current = false;

      if (!isSuccess) {
        setVisible(false);
        setPercent(0);
        console.log('Not success, hiding and resetting');
      }
    }
  }, [isLoading]);

  useEffect(() => {
    if (isSuccess) {
      console.log('Success triggered, completing progress');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setPercent(100);
      setVisible(false); // <-- Immediately hide on success
      wasLoadingRef.current = false;
    }
  }, [isSuccess]);

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <Progress
      percent={percent}
      status={isSuccess ? 'success' : 'active'}
      showInfo={false}
      strokeWidth={8}
      style={{ marginTop: 16 }}
    />
  );
}

export default Loading;
